import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import axios from "axios";
import { Search, Loader2, User, Users, Briefcase, Baby, Scale, ListChecks } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

// Session 348 — main-menu search. Order = the order groups are listed.
const GROUPS = [
  { key: "clients",  label: "Clients",             icon: User },
  { key: "contacts", label: "Contacts",            icon: Users },
  { key: "cases",    label: "Cases",               icon: Briefcase },
  { key: "children", label: "Children",            icon: Baby },
  { key: "divorce",  label: "Divorce information", icon: Scale },
  { key: "tasks",    label: "Tasks",               icon: ListChecks },
];

function useSearchResults(q) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);
  useEffect(() => {
    const term = q.trim();
    const id = ++reqId.current;
    if (term.length < 2) { setResults(null); setLoading(false); return undefined; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/search`, {
          params: { q: term },
          headers: { Authorization: `Bearer ${localStorage.getItem("compass_token")}` },
        });
        if (id === reqId.current) setResults(r.data);
      } catch {
        if (id === reqId.current) setResults({});
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return { results, loading };
}

function ResultRow({ item, icon: Icon, active, onPick, onHover }) {
  return (
    <button
      type="button"
      onClick={() => onPick(item)}
      onMouseMove={onHover}
      data-testid={`global-search-result-${item.type}-${item.id}`}
      className={[
        "flex w-full items-start gap-3 rounded-lg px-3 py-2 text-left transition-colors",
        active ? "bg-[#001C60]/[0.07]" : "hover:bg-gray-50",
      ].join(" ")}
    >
      <Icon size={16} className="mt-0.5 flex-shrink-0 text-[#001C60]/70" />
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-gray-800">{item.title}</span>
        {item.subtitle && <span className="block truncate text-xs text-gray-500">{item.subtitle}</span>}
      </span>
    </button>
  );
}

function ResultGroups({ results, flat, active, setActive, onPick }) {
  return GROUPS.filter((g) => results?.[g.key]?.length).map((g) => (
    <div key={g.key} className="mb-2" data-testid={`global-search-group-${g.key}`}>
      <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{g.label}</p>
      {results[g.key].map((item) => {
        const idx = flat.indexOf(item);
        return (
          <ResultRow key={`${item.type}-${item.id}-${idx}`} item={item} icon={g.icon}
            active={idx === active} onPick={onPick} onHover={() => setActive(idx)} />
        );
      })}
    </div>
  ));
}

export function GlobalSearch({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const { results, loading } = useSearchResults(q);
  const flat = GROUPS.flatMap((g) => results?.[g.key] || []);
  const isStaff = ["Lawyer", "Admin"].includes(localStorage.getItem("compass_user_type"));

  useEffect(() => { if (!open) setQ(""); }, [open]);
  useEffect(() => { setActive(0); }, [results]);

  const pick = (item) => { onOpenChange(false); navigate(item.url); };
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, flat.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    if (e.key === "Enter" && flat[active]) { e.preventDefault(); pick(flat[active]); }
  };
  const scope = isStaff
    ? "your firm's clients, contacts, cases, children, divorce information and tasks"
    : "your contacts, cases, children, divorce information and tasks";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[12vh] max-w-xl translate-y-0 gap-0 overflow-hidden p-0" data-testid="global-search-dialog">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <DialogDescription className="sr-only">Search {scope}</DialogDescription>
        <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 pr-12">
          <Search size={18} className="flex-shrink-0 text-gray-400" />
          <input
            autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
            placeholder="Search names, emails, phones, file numbers, tasks…"
            data-testid="global-search-input"
            className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400"
          />
          {loading && <Loader2 size={16} className="animate-spin text-gray-400" data-testid="global-search-loading" />}
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2" data-testid="global-search-results">
          {q.trim().length < 2 ? (
            <p className="px-3 py-6 text-center text-xs text-gray-400" data-testid="global-search-hint">Search {scope}.</p>
          ) : results && !loading && flat.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-gray-500" data-testid="global-search-empty">No matches for “{q.trim()}”</p>
          ) : (
            <ResultGroups results={results} flat={flat} active={active} setActive={setActive} onPick={pick} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
